import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { ME_QUERY } from '../../apollo/queries/userQueries';
import { CREATE_QUOTATION } from '../../apollo/queries';
import { uploadFileToS3 } from '../../utils/s3Upload';
import '../css/CustomOrderPage.css';

const CustomOrderPageContent = () => {
    const [step, setStep] = useState(1);
    const navigate = useNavigate();

    // 사용자 정보 가져오기
    const { data: userProfileData } = useQuery(ME_QUERY, {
        errorPolicy: 'all'
    });

    // 파일 업로드 상태
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    // 사용자 정보 상태
    const [userInfo, setUserInfo] = useState({
        manager: '',
        department: '',
        email: '',
        phone: '',
        mobile: '',
        fax: '',
        company: ''
    });

    // 추가 요청사항
    const [additionalRequest, setAdditionalRequest] = useState('');

    // 견적 생성 뮤테이션
    const [createQuotation, { loading: creatingQuotation }] = useMutation(CREATE_QUOTATION, {
        onCompleted: (data) => {
            console.log('견적 요청 완료:', data);
            setStep(3);
        },
        onError: (error) => {
            console.error('견적 요청 에러:', error);
            const message = error?.message
                || error?.graphQLErrors?.[0]?.message
                || '';
            if (message.includes('로그인이 필요합니다')) {
                window.dispatchEvent(new CustomEvent('openLoginPanel'));
            } else {
                alert('견적 요청 중 오류가 발생했습니다.');
            }
        }
    });

    // 사용자 프로필 데이터로 폼 초기화
    React.useEffect(() => {
        if (userProfileData?.me) {
            const profile = userProfileData.me;
            setUserInfo({
                manager: profile.name || '',
                department: profile.department || '',
                email: profile.email || '',
                phone: profile.phone || '',
                mobile: profile.mobile || '',
                fax: profile.fax || '',
                company: profile.company || ''
            });
        }
    }, [userProfileData]);

    // 내 정보 불러오기
    const handleLoadUserInfo = () => {
        if (userProfileData?.me) {
            const profile = userProfileData.me;
            setUserInfo({
                manager: profile.name || '',
                department: profile.department || '',
                email: profile.email || '',
                phone: profile.phone || '',
                mobile: profile.mobile || '',
                fax: profile.fax || '',
                company: profile.company || ''
            });
        } else {
            alert('로그인이 필요합니다.');
        }
    };

    // 입력값 변경 처리
    const handleInputChange = (field, value) => {
        setUserInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 파일 업로드 처리
    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        
        try {
            const uploadPromises = files.map(async (file) => {
                // 파일 유효성 검사 (CAD 파일, 이미지 파일, 문서 파일)
                const allowedTypes = [
                    // 이미지 파일
                    'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/tiff',
                    // 문서 파일
                    'application/pdf',
                    // CAD 파일들
                    'application/octet-stream', // 일반적인 바이너리 파일 (DWG, DXF 등)
                    'application/x-autocad', // AutoCAD 파일
                    'image/vnd.dwg', // DWG 파일
                    'application/x-dwg', // DWG 파일
                    'application/dxf', // DXF 파일
                    'application/x-dxf', // DXF 파일
                    'application/step', // STEP 파일
                    'application/x-step', // STEP 파일
                    'model/step', // STEP 파일
                    'application/iges', // IGES 파일
                    'application/x-iges', // IGES 파일
                    'model/iges', // IGES 파일
                ];
                
                // 파일 확장자도 확인
                const fileExtension = file.name.toLowerCase().split('.').pop();
                const allowedExtensions = [
                    // 이미지
                    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif',
                    // 문서
                    'pdf',
                    // CAD
                    'dwg', 'dxf', 'step', 'stp', 'iges', 'igs', 'sat', 'x_t', 'x_b',
                    // 3D 모델
                    'obj', 'stl', 'ply', '3ds', 'fbx'
                ];

                // 파일 크기 검사 (50MB 제한)
                const maxSize = 50 * 1024 * 1024;
                if (file.size > maxSize) {
                    throw new Error(`파일 크기가 너무 큽니다: ${file.name}\n최대 50MB까지 업로드 가능합니다.`);
                }

                // 파일 타입 또는 확장자 검증
                const isValidType = allowedTypes.includes(file.type);
                const isValidExtension = allowedExtensions.includes(fileExtension);
                
                if (!isValidType && !isValidExtension) {
                    throw new Error(`지원하지 않는 파일 형식입니다: ${file.name}\n지원 형식: 이미지(JPG, PNG, GIF 등), 문서(PDF), CAD(DWG, DXF, STEP 등)`);
                }
                
                // 커스텀 유효성 검사만 사용 (validateFile 함수 사용하지 않음)

                const uploadedUrl = await uploadFileToS3(file, 'custom-orders');
                
                return {
                    name: file.name,
                    url: uploadedUrl,
                    size: file.size,
                    type: file.type || `application/${fileExtension}`,
                    extension: fileExtension
                };
            });

            const uploadedFileResults = await Promise.all(uploadPromises);
            setUploadedFiles(prev => [...prev, ...uploadedFileResults]);
            
            // 파일 입력 초기화
            event.target.value = '';
            
        } catch (error) {
            console.error('파일 업로드 에러:', error);
            alert(`파일 업로드 실패: ${error.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    // 파일 제거
    const handleRemoveFile = (index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // 폼 유효성 검사
    const validateForm = () => {
        const requiredFields = ['manager', 'email', 'phone', 'mobile'];
        for (const field of requiredFields) {
            if (!userInfo[field] || userInfo[field].trim() === '') {
                alert(`${field === 'manager' ? '담당자' : field === 'email' ? '이메일' : field === 'phone' ? '전화번호' : '휴대전화'}는 필수 입력 항목입니다.`);
                return false;
            }
        }

        if (uploadedFiles.length === 0) {
            alert('도면이나 파일을 최소 1개 이상 첨부해주세요.');
            return false;
        }

        return true;
    };

    const handleNextStep = () => {
        if (step === 1) {
            if (!validateForm()) return;
        }
        setStep(step + 1);
    };

    const handlePrevStep = () => setStep(step - 1);
    const handleReset = () => {
        setStep(1);
        setUploadedFiles([]);
        setAdditionalRequest('');
    };

    // 견적 요청 제출
    const handleSubmitQuotation = () => {
        if (!validateForm()) return;

        // 파일 정보와 사용자 정보를 포함한 메시지 생성
        let message = `=== 주문 제작 견적 요청 ===\n\n`;
        message += `담당자: ${userInfo.manager}\n`;
        message += `회사명: ${userInfo.company}\n`;
        message += `부서: ${userInfo.department}\n`;
        message += `이메일: ${userInfo.email}\n`;
        message += `전화번호: ${userInfo.phone}\n`;
        message += `휴대전화: ${userInfo.mobile}\n`;
        if (userInfo.fax) message += `팩스: ${userInfo.fax}\n`;
        
        message += `\n=== 첨부 파일 ===\n`;
        uploadedFiles.forEach((file, index) => {
            message += `${index + 1}. ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)\n`;
            message += `   다운로드: ${file.url}\n`;
        });
        
        if (additionalRequest.trim()) {
            message += `\n=== 추가 요청사항 ===\n${additionalRequest.trim()}`;
        }

        // 첨부파일 정보 배열 생성 (JSON.stringify 하지 않음)
        const customerFileUrls = uploadedFiles;

        // 실제 GraphQL 뮤테이션 실행
        console.log('견적 요청 데이터:', {
            message: message,
            items: null,
            customerFiles: customerFileUrls.length > 0 ? customerFileUrls : null
        });
        
        createQuotation({
            variables: {
                message: message,
                items: null, // 주문 제작은 기존 상품이 아니므로 null
                customerFiles: customerFileUrls.length > 0 ? customerFileUrls : null
            }
        });
    };

    const renderStep1 = () => (
        <div className="co-step-container">
            <h2 className="co-title">주문 제작</h2>
            <p className="co-description">견적이 필요한 도면 및 파일을 아래에 첨부해 주세요. 빠른 대응 도와드리겠습니다.</p>
            
            <div className="file-upload-section">
                <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    id="file-upload"
                    accept=".dwg,.dxf,.step,.stp,.iges,.igs,.sat,.x_t,.x_b,.obj,.stl,.ply,.3ds,.fbx,.pdf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif"
                />
                <div className="upload-btn-wrapper">
                    <button 
                        className="upload-btn" 
                        onClick={() => document.getElementById('file-upload').click()}
                        disabled={isUploading}
                    >
                        <div>{isUploading ? '업로드 중...' : '도면 첨부'}</div>
                    </button>
                </div>
                <div className="upload-text-wrapper">
                    <p className="upload-main-text">여기에 CAD파일 또는 이미지 파일을 드롭하여 주십시오.</p>
                    <p className="upload-note">여러개의 파일을 업로드 할 수 있습니다.</p>
                </div>
                
                {uploadedFiles.length > 0 && (
                    <div className="uploaded-files">
                        <h4>업로드된 파일:</h4>
                        {uploadedFiles.map((file, index) => (
                            <div key={index} className="file-item">
                                <span className="file-name">{file.name}</span>
                                <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)}MB)</span>
                                <button onClick={() => handleRemoveFile(index)} className="remove-file-btn">삭제</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="form-header">
                <div className="form-header-inner">
                    <h3>담당자 정보</h3>
                    <button className="load-info-btn" onClick={handleLoadUserInfo}>내정보 불러오기</button>
                </div>
                <div className="form-header-divider"></div>
            </div>
            <div className="form-grid">
                <div className="form-row">
                    <label htmlFor="manager">
                        <span>담당자</span>
                        <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                        <input 
                            type="text" 
                            id="manager"
                            value={userInfo.manager}
                            onChange={(e) => handleInputChange('manager', e.target.value)}
                            placeholder="예 : 홍길동" 
                        />
                    </div>
                </div>
                <div className="form-row">
                    <label htmlFor="department">부서 / 역할</label>
                    <div className="input-wrapper">
                        <input 
                            type="text" 
                            id="department"
                            value={userInfo.department}
                            onChange={(e) => handleInputChange('department', e.target.value)}
                            placeholder="예 : 구매 담당자" 
                        />
                    </div>
                </div>
                <div className="form-row">
                    <label htmlFor="email">이메일 주소</label>
                    <div className="input-wrapper">
                        <input 
                            type="email" 
                            id="email"
                            value={userInfo.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="예 : abck@gaerfe.com" 
                        />
                    </div>
                </div>
                <div className="form-row phone-row">
                    <div className="phone-group">
                        <label htmlFor="phone">
                            <span>전화번호</span>
                            <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                            <input 
                                type="tel" 
                                id="phone"
                                value={userInfo.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                placeholder="예 : 02-1234-5478" 
                            />
                        </div>
                    </div>
                    <div className="phone-group">
                        <label htmlFor="mobile">
                            <span>휴대전화</span>
                            <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                            <input 
                                type="tel" 
                                id="mobile"
                                value={userInfo.mobile}
                                onChange={(e) => handleInputChange('mobile', e.target.value)}
                                placeholder="예 : 010-6543-1875" 
                            />
                        </div>
                    </div>
                </div>
                <div className="form-row">
                    <label htmlFor="fax">FAX</label>
                    <div className="input-wrapper">
                        <input 
                            type="tel" 
                            id="fax"
                            value={userInfo.fax}
                            onChange={(e) => handleInputChange('fax', e.target.value)}
                            placeholder="예 : 02-1234-5478" 
                        />
                    </div>
                </div>
            </div>
            <div className="button-container">
                <button className="submit-btn" onClick={handleNextStep}>
                    <div>입력 완료</div>
                </button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="co-step-container">
            <h2 className="co-title">입력한 내용 확인</h2>
            
            <div className="confirmation-section">
                <h3>담당자 정보</h3>
                <div className="form-grid">
                    <div className="form-row">
                        <label>
                            <span>담당자</span>
                        </label>
                        <div className="input-wrapper">
                            <div className="form-display-value">{userInfo.manager}</div>
                        </div>
                    </div>
                    <div className="form-row">
                        <label>부서 / 역할</label>
                        <div className="input-wrapper">
                            <div className="form-display-value">{userInfo.department || '미입력'}</div>
                        </div>
                    </div>
                    <div className="form-row">
                        <label>이메일 주소</label>
                        <div className="input-wrapper">
                            <div className="form-display-value">{userInfo.email}</div>
                        </div>
                    </div>
                    <div className="form-row phone-row">
                        <div className="phone-group">
                            <label>
                                <span>전화번호</span>
                            </label>
                            <div className="input-wrapper">
                                <div className="form-display-value">{userInfo.phone}</div>
                            </div>
                        </div>
                        <div className="phone-group">
                            <label>
                                <span>휴대전화</span>
                            </label>
                            <div className="input-wrapper">
                                <div className="form-display-value">{userInfo.mobile}</div>
                            </div>
                        </div>
                    </div>
                    <div className="form-row">
                        <label>FAX</label>
                        <div className="input-wrapper">
                            <div className="form-display-value">{userInfo.fax || '미입력'}</div>
                        </div>
                    </div>
                    <div className="form-row">
                        <label>회사명</label>
                        <div className="input-wrapper">
                            <div className="form-display-value">{userInfo.company || '미입력'}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="confirmation-section">
                <h3>첨부 파일</h3>
                <div className="files-list">
                    {uploadedFiles.map((file, index) => (
                        <div key={index} className="file-confirmation-item">
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)}MB)</span>
                        </div>
                    ))}
                </div>
            </div>
            
            {additionalRequest && (
                <div className="confirmation-section">
                    <h3>추가 요청사항</h3>
                    <p className="additional-request-text">{additionalRequest}</p>
                </div>
            )}

            <div className="button-container">
                <button className="prev-btn" onClick={handlePrevStep}>이전</button>
                <button 
                    className="submit-btn" 
                    onClick={handleSubmitQuotation}
                    disabled={creatingQuotation}
                >
                    {creatingQuotation ? '견적 요청 중...' : '견적 요청'}
                </button>
            </div>
        </div>
    );

    const renderStep3 = () => {
        const userEmail = userInfo.email || userProfileData?.me?.email || '';
        return (
            <div className="co-step-container">
                <div className="completion-message">
                    <h2 className="completion-title">견적 요청 등록이 완료되었습니다.</h2>
                    <p className="completion-description">
                        견적서는 나의 견적이력에서 확인할 수 있습니다. 작성하신 [{userEmail}] 메일주소로 함께 발송되는 점 참고바랍니다.
                    </p>
                </div>
                <div className="button-container step3-button">
                    <button className="back-to-list-btn" onClick={() => navigate('/quotation-history')}>목록으로 돌아가기</button>
                </div>
            </div>
        );
    };

    return (
        <div className="custom-order-page">
            <div className="co-breadcrumb">
                <span>홈</span> &gt; <span>주문 제작</span> 
                {step > 1 && <> &gt; <span>입력한 내용 확인</span></>}
                {step > 2 && <> &gt; <span>견적 요청 완료</span></>}
            </div>
             <div className="co-progress-indicator">
                <div className="co-progress-line-bg"></div>
                <div className={`co-progress-line-fill ${step >= 2 ? 'step2' : ''} ${step >= 3 ? 'step3' : ''}`}></div>
                <div className={`step-item ${step >= 1 ? 'active' : ''}`}>
                    <div className={`step-dot ${step >= 1 ? 'active' : ''}`}></div>
                    <div className="step-label">도면첨부</div>
                </div>
                <div className={`step-item ${step >= 2 ? 'active' : ''}`}>
                    <div className={`step-dot ${step >= 2 ? 'active' : ''}`}></div>
                    <div className="step-label">입력한 내용 확인</div>
                </div>
                <div className={`step-item ${step >= 3 ? 'active' : ''}`}>
                    <div className={`step-dot ${step >= 3 ? 'active' : ''}`}></div>
                    <div className="step-label">견적 요청 완료</div>
                </div>
            </div>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
        </div>
    );
};

const CustomOrderPage = () => {
    return (
        <CustomOrderPageContent />
    );
};


export default CustomOrderPage; 